import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Package } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import Badge from '@/components/ui/Badge';

interface BundleItem {
  product_id: string;
  product_name: string;
  product_slug: string;
  price: number;
  quantity: number;
  image_url?: string;
}

interface Bundle {
  id: string;
  name: string;
  slug: string;
  items: BundleItem[];
  total_price: number;
  discounted_price: number;
  discount_type: string;
  discount_value: number;
}

export default function ProductBundles({ productId }: { productId: string }) {
  const { data } = useQuery({
    queryKey: ['bundles-by-product', productId],
    queryFn: () => api.get<{ bundles: Bundle[] }>(`/bundles/by-product/${productId}`),
    enabled: !!productId,
  });

  const bundles = data?.bundles ?? [];
  if (bundles.length === 0) return null;

  return (
    <section className="mt-12" aria-labelledby="bundles-heading">
      <h2 id="bundles-heading" className="flex items-center gap-2 text-lg font-bold text-ink mb-4">
        <Package className="h-5 w-5 text-sage-700" aria-hidden="true" />
        Save with a Bundle
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {bundles.map((bundle) => {
          const savings = bundle.total_price - bundle.discounted_price;
          return (
            <Link
              key={bundle.id}
              to={`/bundles/${bundle.slug}`}
              className="group rounded-xl border border-line bg-surface p-5 shadow-sm transition-shadow hover:shadow-md"
            >
              <h3 className="font-semibold text-ink group-hover:text-sage-700 transition-colors">
                {bundle.name}
              </h3>
              <p className="mt-1 text-sm text-ink-muted">
                {bundle.items?.length || 0} items included
              </p>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-lg font-bold text-sage-700">
                  {formatCurrency(bundle.discounted_price)}
                </span>
                {savings > 0 && (
                  <>
                    <span className="text-sm text-ink-muted line-through">
                      {formatCurrency(bundle.total_price)}
                    </span>
                    <Badge variant="green">
                      Save {formatCurrency(savings)}
                    </Badge>
                  </>
                )}
              </div>
              <span className="mt-3 inline-block text-sm font-medium text-sage-700 group-hover:underline">
                View Bundle →
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
